import { useState } from 'react';
import { jsPDF } from 'jspdf';

function ProcedureDetail({ procedure, roles, onBack, onEditStep, onDeleteStep, onAddStep, onReorderStep, onDelete, onEdit, onGetChangeLog }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [addingAfter, setAddingAfter] = useState(null); // index or -1 for end
  const [newTitle, setNewTitle] = useState('');
  const [newDetails, setNewDetails] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaName, setMetaName] = useState(procedure.name);
  const [metaDesc, setMetaDesc] = useState(procedure.description);
  const [metaCategory, setMetaCategory] = useState(procedure.category);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const role = roles.find(r => r.id === procedure.roleTag);

  async function loadChangeLog() {
    if (!onGetChangeLog) return;
    setLogLoading(true);
    try {
      const log = await onGetChangeLog(procedure.id);
      setChangeLog(log);
      setShowChangeLog(true);
    } catch { /* ignore */ }
    finally { setLogLoading(false); }
  }

  function startEdit(step, index) {
    setEditingIndex(index);
    setEditTitle(step.title);
    setEditDetails(step.details);
    setEditNotes(step.notes || '');
  }

  async function saveEdit() {
    await onEditStep(procedure.id, editingIndex, { title: editTitle, details: editDetails, notes: editNotes });
    setEditingIndex(null);
  }

  async function handleAdd() {
    if (!newTitle.trim()) return;
    await onAddStep(procedure.id, { title: newTitle.trim(), details: newDetails.trim(), notes: newNotes.trim() }, addingAfter === -1 ? null : addingAfter);
    setNewTitle('');
    setNewDetails('');
    setNewNotes('');
    setAddingAfter(null);
  }

  async function handleDelete(index) {
    if (!window.confirm(`Delete step ${index + 1}: "${procedure.steps[index]?.title}"?`)) return;
    await onDeleteStep(procedure.id, index);
  }

  async function handleDeleteProcedure() {
    if (!window.confirm(`Delete procedure "${procedure.name}"? This cannot be undone.`)) return;
    await onDelete(procedure.id);
    onBack();
  }

  function exportToPdf() {
    const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
    const margin = 40;
    const pageW = 612;
    const maxW = pageW - margin * 2;
    let y = margin;

    // Title
    pdf.setFontSize(18);
    pdf.setTextColor(75, 0, 130);
    pdf.text(procedure.name, margin, y);
    y += 24;

    if (procedure.description) {
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      const descLines = pdf.splitTextToSize(procedure.description, maxW);
      pdf.text(descLines, margin, y);
      y += descLines.length * 13 + 8;
    }

    pdf.setDrawColor(184, 134, 11);
    pdf.line(margin, y, pageW - margin, y);
    y += 16;

    // Steps
    procedure.steps.forEach((step) => {
      if (y > 720) { pdf.addPage(); y = margin; }

      pdf.setFontSize(11);
      pdf.setTextColor(75, 0, 130);
      pdf.text(`${step.stepNumber}.`, margin, y);
      pdf.setTextColor(30);
      pdf.setFont(undefined, 'bold');
      const titleLines = pdf.splitTextToSize(step.title, maxW - 25);
      pdf.text(titleLines, margin + 22, y);
      pdf.setFont(undefined, 'normal');
      y += titleLines.length * 14 + 4;

      if (step.details) {
        if (y > 720) { pdf.addPage(); y = margin; }
        pdf.setFontSize(10);
        pdf.setTextColor(80);
        const detailLines = pdf.splitTextToSize(step.details, maxW - 25);
        pdf.text(detailLines, margin + 22, y);
        y += detailLines.length * 13 + 4;
      }

      if (step.notes) {
        if (y > 720) { pdf.addPage(); y = margin; }
        pdf.setFontSize(9);
        pdf.setTextColor(184, 134, 11);
        pdf.setFont(undefined, 'italic');
        const noteLines = pdf.splitTextToSize('Note: ' + step.notes, maxW - 25);
        pdf.text(noteLines, margin + 22, y);
        pdf.setFont(undefined, 'normal');
        y += noteLines.length * 12 + 8;
      }

      y += 6;
    });

    // Footer
    if (y > 720) { pdf.addPage(); y = margin; }
    y += 10;
    pdf.setFontSize(8);
    pdf.setTextColor(150);
    pdf.text(`Generated ${new Date().toLocaleDateString()} — DefineHoly.com`, margin, y);

    pdf.save(`${procedure.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  }

  async function saveMetaEdit() {
    await onEdit(procedure.id, { Title: metaName, Description: metaDesc, Category: metaCategory });
    setEditingMeta(false);
  }

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div className="ProcDetail">
      <button className="ProcDetail-back" onClick={onBack}>&larr; Back to Procedures</button>

      <div className="ProcDetail-header">
        {role && <span className="ProcList-card-dot" style={{ background: role.color }} />}
        {editingMeta ? (
          <div className="ProcDetail-meta-edit">
            <input className="Fitness-input" value={metaName} onChange={e => setMetaName(e.target.value)} placeholder="Name" />
            <input className="Fitness-input Fitness-input--small" value={metaCategory} onChange={e => setMetaCategory(e.target.value)} placeholder="Category (e.g. Business/Insurance)" />
            <input className="Fitness-input Fitness-input--small" value={metaDesc} onChange={e => setMetaDesc(e.target.value)} placeholder="Description" />
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              <button className="Fitness-btn Fitness-btn--small" onClick={saveMetaEdit}>Save</button>
              <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setEditingMeta(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="ProcDetail-name">{procedure.name}</h2>
            <div className="ProcDetail-meta">
              {procedure.category && <span>{procedure.category}</span>}
              <span>{procedure.steps.length} steps</span>
              <button className="Fitness-btn-icon" onClick={() => setEditingMeta(true)} title="Edit">&#9998;</button>
              <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={handleDeleteProcedure} title="Delete">&times;</button>
              {procedure.steps.length > 0 && <button className="ProcDetail-export-btn" onClick={exportToPdf} title="Export PDF">PDF</button>}
            </div>
          </>
        )}
        {procedure.description && !editingMeta && <p className="ProcDetail-desc">{procedure.description}</p>}
      </div>

      <div className="ProcDetail-steps">
        {procedure.steps.map((step, i) => (
          <div key={i} className="ProcDetail-step">
            {editingIndex === i ? (
              <div className="ProcDetail-step-edit">
                <input className="Fitness-input" value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Step title" />
                <textarea className="ProcDetail-step-textarea" value={editDetails} onChange={e => setEditDetails(e.target.value)} placeholder="Details" rows={2} />
                <input className="Fitness-input Fitness-input--small" value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Notes (optional)" />
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <button className="Fitness-btn Fitness-btn--small" onClick={saveEdit}>Save</button>
                  <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setEditingIndex(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="ProcDetail-step-header">
                  <span className="ProcDetail-step-num">{step.stepNumber}</span>
                  <span className="ProcDetail-step-title">{step.title}</span>
                </div>
                {step.details && <p className="ProcDetail-step-details">{step.details}</p>}
                {step.notes && <p className="ProcDetail-step-notes">Note: {step.notes}</p>}
                <div className="ProcDetail-step-actions">
                  <button className="Fitness-btn-icon" onClick={() => startEdit(step, i)} title="Edit">&#9998;</button>
                  <button className="Fitness-btn-icon" onClick={() => { if (i > 0) onReorderStep(procedure.id, i, i - 1); }} disabled={i === 0} title="Move up">&#9650;</button>
                  <button className="Fitness-btn-icon" onClick={() => { if (i < procedure.steps.length - 1) onReorderStep(procedure.id, i, i + 1); }} disabled={i === procedure.steps.length - 1} title="Move down">&#9660;</button>
                  <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => handleDelete(i)} title="Delete">&times;</button>
                  <button className="ProcDetail-add-after" onClick={() => setAddingAfter(i)}>+ after</button>
                </div>
              </>
            )}

            {addingAfter === i && (
              <div className="ProcDetail-add-form">
                <input className="Fitness-input" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="New step title" autoFocus />
                <textarea className="ProcDetail-step-textarea" value={newDetails} onChange={e => setNewDetails(e.target.value)} placeholder="Details" rows={2} />
                <input className="Fitness-input Fitness-input--small" value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Notes (optional)" />
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <button className="Fitness-btn Fitness-btn--small" onClick={handleAdd}>Add Step</button>
                  <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setAddingAfter(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <button className="ProcDetail-add-end" onClick={() => setAddingAfter(-1)}>+ Add Step</button>

      {addingAfter === -1 && (
        <div className="ProcDetail-add-form">
          <input className="Fitness-input" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="New step title" autoFocus />
          <textarea className="ProcDetail-step-textarea" value={newDetails} onChange={e => setNewDetails(e.target.value)} placeholder="Details" rows={2} />
          <input className="Fitness-input Fitness-input--small" value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Notes (optional)" />
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <button className="Fitness-btn Fitness-btn--small" onClick={handleAdd}>Add Step</button>
            <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setAddingAfter(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Change log — tucked away */}
      <div className="ProcDetail-changelog-toggle">
        <button className="ProcDetail-changelog-btn" onClick={loadChangeLog} disabled={logLoading}>
          {logLoading ? 'Loading...' : showChangeLog ? 'Hide Changes' : 'View Changes'}
        </button>
      </div>

      {showChangeLog && (
        <div className="ProcDetail-changelog">
          <div className="ProcDetail-changelog-header">
            <span>Change History</span>
            <button className="Fitness-btn-icon" onClick={() => setShowChangeLog(false)}>&times;</button>
          </div>
          {changeLog.length === 0 && <p style={{ color: '#999', fontSize: '0.8rem' }}>No changes recorded.</p>}
          {changeLog.map((entry, i) => (
            <div key={i} className="ProcDetail-changelog-entry">
              <div className="ProcDetail-changelog-type">{entry.changeType.replace(/_/g, ' ')}</div>
              <div className="ProcDetail-changelog-detail">
                {entry.stepNumber > 0 && <span>Step {entry.stepNumber}</span>}
                {entry.oldValue && <span className="ProcDetail-changelog-old">{entry.oldValue.substring(0, 60)}</span>}
                {entry.newValue && <span className="ProcDetail-changelog-new">{entry.newValue.substring(0, 60)}</span>}
              </div>
              <div className="ProcDetail-changelog-meta">
                {entry.changedBy} &middot; {formatDate(entry.changedDate)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProcedureDetail;
